/**
 * Delivery & Pickup purchase flow for the product page.
 *
 * Self-contained, dependency-free custom element (<product-delivery-pickup>).
 * It does not depend on the theme's compiled vendor bundle (xo-webcomponents.js) —
 * it only relies on Shopify's built-in line item property mechanism: any
 * `properties[...]`-named field inside the product's <xo-product> wrapper is
 * automatically collected and sent to /cart/add.js by the theme's own
 * add-to-cart handler.
 *
 * Responsibilities:
 *  1. Toggle the Delivery / Pickup panel based on the selected method.
 *  2. Generate time slots for the selected date from DELIVERY_PICKUP_CONFIG,
 *     filtering out any slot that starts sooner than the minimum lead time.
 *  3. Keep the Add to Cart button's enabled/disabled state in sync with the
 *     current selection (method + date + time + recipient name, plus
 *     recipient phone when Delivery is selected), both visually and by way of
 *     the native HTML5 "required" constraint (see product-delivery-pickup.liquid,
 *     which associates every required field with the add-to-cart form via a
 *     `form="..."` attribute, and relies on the browser excluding a field from
 *     validation while its containing panel carries the native `hidden`
 *     attribute — this is what makes only the *selected* method's date/time/
 *     recipient phone required).
 */
(() => {
  'use strict';

  // -------------------------------------------------------------------
  // 1. CONFIGURATION
  //
  // Every business rule for this feature lives here. Nothing below this
  // object should ever need to change to adjust hours, slot lengths, or
  // lead time — edit these values only.
  // -------------------------------------------------------------------
  const DELIVERY_PICKUP_CONFIG = {
    // Minimum number of hours between "now" and a slot's start time before
    // that slot can be selected. Applies to both delivery and pickup.
    leadTimeHours: 2,

    // How many days into the future the date pickers allow scheduling.
    maxDaysAhead: 60,

    // Per-method business hours, expressed as a list of time slots.
    // Times are 24-hour "HH:MM" local time. Slot labels (e.g. "12:00 PM –
    // 2:00 PM") are generated automatically from start/end — add, remove,
    // resize, or retime slots freely, in any order.
    //
    // Both methods use rolling 2-hour windows that start every hour within
    // business hours (delivery: 12:00–18:00, pickup: 10:00–17:00).
    methods: {
      delivery: {
        slots: [
          { start: '12:00', end: '14:00' },
          { start: '13:00', end: '15:00' },
          { start: '14:00', end: '16:00' },
          { start: '15:00', end: '17:00' },
          { start: '16:00', end: '18:00' }
        ]
      },
      pickup: {
        slots: [
          { start: '10:00', end: '12:00' },
          { start: '11:00', end: '13:00' },
          { start: '12:00', end: '14:00' },
          { start: '13:00', end: '15:00' },
          { start: '14:00', end: '16:00' },
          { start: '15:00', end: '17:00' }
        ]
      }
    }
  };

  // How often (ms) to silently re-check availability while the page is
  // open, so a slot that expires mid-visit is removed without a refresh.
  const AVAILABILITY_WATCH_INTERVAL_MS = 60 * 1000;

  const METHOD_KEYS = ['delivery', 'pickup'];
  const PROPERTY_NAME_BY_METHOD = {
    delivery: 'Delivery Time',
    pickup: 'Pickup Time'
  };

  // -------------------------------------------------------------------
  // 2. Pure date/time helpers — no DOM access, easy to reason about
  //    and to unit test in isolation if needed.
  // -------------------------------------------------------------------

  const pad2 = (value) => String(value).padStart(2, '0');

  /** Formats a Date as "YYYY-MM-DD" using its LOCAL time fields. */
  const toISODate = (date) =>
    `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

  /** Builds a local Date from a "YYYY-MM-DD" date string and "HH:MM" time. */
  const combineDateAndTime = (isoDateStr, hhmm) => {
    const [year, month, day] = isoDateStr.split('-').map(Number);
    const [hour, minute] = hhmm.split(':').map(Number);
    return new Date(year, month - 1, day, hour, minute, 0, 0);
  };

  const addDays = (date, days) => {
    const result = new Date(date.getTime());
    result.setDate(result.getDate() + days);
    return result;
  };

  /** "14:30" -> "2:30 PM" */
  const formatTimeOfDay = (hhmm) => {
    const [hour, minute] = hhmm.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${hour12}:${pad2(minute)} ${period}`;
  };

  const formatSlotLabel = (slot) => `${formatTimeOfDay(slot.start)} – ${formatTimeOfDay(slot.end)}`;

  const formatDisplayDate = (isoDateStr) => {
    const date = combineDateAndTime(isoDateStr, '00:00');
    try {
      return date.toLocaleDateString(undefined, {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (err) {
      return isoDateStr;
    }
  };

  /** True when `slot`, on `isoDateStr`, starts at/after now + lead time. */
  const isSlotBookable = (isoDateStr, slot, now) => {
    const slotStart = combineDateAndTime(isoDateStr, slot.start);
    const cutoff = new Date(now.getTime() + DELIVERY_PICKUP_CONFIG.leadTimeHours * 60 * 60 * 1000);
    return slotStart.getTime() >= cutoff.getTime();
  };

  const getMethodConfig = (methodKey) => DELIVERY_PICKUP_CONFIG.methods[methodKey] || null;

  /** Slots for `methodKey` on `isoDateStr` that still satisfy the lead time. */
  const getBookableSlots = (methodKey, isoDateStr, now) => {
    const method = getMethodConfig(methodKey);
    if (!method || !isoDateStr) return [];
    return method.slots.filter((slot) => isSlotBookable(isoDateStr, slot, now));
  };

  const hasBookableSlotsToday = (methodKey, now) =>
    getBookableSlots(methodKey, toISODate(now), now).length > 0;

  /** Earliest date the customer may pick for `methodKey`, given "now". Rolls
   * forward to tomorrow automatically once today's slots have run out. */
  const getMinSelectableDate = (methodKey, now) =>
    hasBookableSlotsToday(methodKey, now) ? toISODate(now) : toISODate(addDays(now, 1));

  const getMaxSelectableDate = (now) => toISODate(addDays(now, DELIVERY_PICKUP_CONFIG.maxDaysAhead));

  // -------------------------------------------------------------------
  // 3. <product-delivery-pickup> custom element
  // -------------------------------------------------------------------

  class ProductDeliveryPickup extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.formId = this.getAttribute('data-form-id') || '';
      this.selectedMethod = null;

      this.methodInputs = Array.from(this.querySelectorAll('[data-method-input]'));
      this.panels = {
        delivery: this.querySelector('[data-panel="delivery"]'),
        pickup: this.querySelector('[data-panel="pickup"]')
      };
      this.dateInputs = {
        delivery: this.querySelector('[data-date-input="delivery"]'),
        pickup: this.querySelector('[data-date-input="pickup"]')
      };
      this.dateMirrors = {
        delivery: this.querySelector('[data-date-mirror="delivery"]'),
        pickup: this.querySelector('[data-date-mirror="pickup"]')
      };
      this.slotContainers = {
        delivery: this.querySelector('[data-slot-container="delivery"]'),
        pickup: this.querySelector('[data-slot-container="pickup"]')
      };
      this.slotMessages = {
        delivery: this.querySelector('[data-slot-message="delivery"]'),
        pickup: this.querySelector('[data-slot-message="pickup"]')
      };
      this.recipientNameInput = this.querySelector('[data-recipient-name]');
      this.recipientPhoneInput = this.querySelector('[data-recipient-phone]');
      this.submitEl = this.querySelector('[data-submit]');
      this.submitButton = this.submitEl ? this.submitEl.querySelector('button') : null;
      this.submitHint = this.querySelector('[data-submit-hint]');

      this.bindEvents();
      this.applyDateBounds();
      this.updateSubmitState();
      this.startAvailabilityWatcher();
    }

    disconnectedCallback() {
      if (this._watcherId) {
        clearInterval(this._watcherId);
        this._watcherId = null;
      }
    }

    bindEvents() {
      this.methodInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.handleMethodChange(input.getAttribute('data-method-input'));
        });
      });

      METHOD_KEYS.forEach((methodKey) => {
        const dateInput = this.dateInputs[methodKey];
        if (!dateInput) return;

        dateInput.addEventListener('change', () => this.handleDateChange(methodKey));

        // Enter has no useful role in a native date field, and this input is
        // form-associated rather than a descendant of <form>; guard against
        // an accidental implicit submit while typing a date.
        dateInput.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') event.preventDefault();
        });
      });

      METHOD_KEYS.forEach((methodKey) => {
        const container = this.slotContainers[methodKey];
        if (!container) return;
        container.addEventListener('change', (event) => {
          if (event.target instanceof HTMLInputElement && event.target.type === 'radio') {
            this.updateSubmitState();
          }
        });
      });

      if (this.recipientNameInput) {
        this.recipientNameInput.addEventListener('input', () => this.updateSubmitState());
      }
      if (this.recipientPhoneInput) {
        this.recipientPhoneInput.addEventListener('input', () => this.updateSubmitState());
      }
    }

    /** Sets each date input's min/max bounds based on the current time. */
    applyDateBounds() {
      const now = new Date();
      METHOD_KEYS.forEach((methodKey) => {
        const dateInput = this.dateInputs[methodKey];
        if (!dateInput) return;

        const min = getMinSelectableDate(methodKey, now);
        dateInput.min = min;
        dateInput.max = getMaxSelectableDate(now);

        // If a previously chosen date fell before the new minimum (e.g. it
        // was "today" and today's slots have since run out while this tab
        // was open), clear it and tell the customer why.
        if (dateInput.value && dateInput.value < min) {
          this.resetDateSelection(methodKey, { showEmptyMessage: true });
        }
      });
    }

    handleMethodChange(methodKey) {
      this.selectedMethod = methodKey;
      METHOD_KEYS.forEach((key) => {
        const panel = this.panels[key];
        if (!panel) return;
        const isActive = key === methodKey;
        panel.hidden = !isActive;
        panel.setAttribute('aria-hidden', String(!isActive));
      });
      this.updateSubmitState();
    }

    handleDateChange(methodKey) {
      const dateInput = this.dateInputs[methodKey];
      const mirror = this.dateMirrors[methodKey];
      if (mirror) {
        mirror.value = dateInput && dateInput.value ? formatDisplayDate(dateInput.value) : '';
      }
      this.renderSlots(methodKey);
      this.updateSubmitState();
    }

    /** Clears a method's date/time selection. When `showEmptyMessage` is
     * true, also shows the "no slots left" text — used when a date the
     * customer actively selected has since become unavailable, as opposed
     * to the neutral empty state before any date has been chosen. Directly
     * clears the DOM rather than calling renderSlots() again, so an
     * already-visible empty-message doesn't get immediately hidden by a
     * second pass through the "no date selected" branch. */
    resetDateSelection(methodKey, { showEmptyMessage = false } = {}) {
      const dateInput = this.dateInputs[methodKey];
      const mirror = this.dateMirrors[methodKey];
      const container = this.slotContainers[methodKey];
      const messageEl = this.slotMessages[methodKey];

      if (dateInput) dateInput.value = '';
      if (mirror) mirror.value = '';
      if (container) container.innerHTML = '';
      if (messageEl) {
        if (showEmptyMessage) {
          messageEl.hidden = false;
          messageEl.textContent = messageEl.getAttribute('data-empty-text') || '';
        } else {
          messageEl.hidden = true;
        }
      }
    }

    /** (Re)builds the time-slot radio buttons for the given method/date.
     * Skips the rebuild entirely when the bookable set hasn't actually
     * changed since the last render (e.g. on the periodic availability
     * re-check below), and preserves the customer's current selection
     * across a real rebuild when that slot is still bookable — otherwise
     * a customer who takes over a minute to fill out the form would find
     * their selection silently cleared with no visible change to explain
     * why. */
    renderSlots(methodKey) {
      const container = this.slotContainers[methodKey];
      const messageEl = this.slotMessages[methodKey];
      const dateInput = this.dateInputs[methodKey];
      if (!container || !dateInput) return;

      const isoDateStr = dateInput.value;
      if (!isoDateStr) {
        container.innerHTML = '';
        if (messageEl) messageEl.hidden = true;
        return;
      }

      const now = new Date();
      const slots = getBookableSlots(methodKey, isoDateStr, now);

      if (!slots.length) {
        // Requirement: if a date has no remaining slots, force another day.
        const nextDay = toISODate(addDays(combineDateAndTime(isoDateStr, '00:00'), 1));
        dateInput.min = nextDay;
        this.resetDateSelection(methodKey, { showEmptyMessage: true });
        return;
      }

      const newLabels = slots.map(formatSlotLabel);
      const currentLabels = Array.from(container.querySelectorAll('input[type="radio"]')).map((el) => el.value);
      const unchanged =
        newLabels.length === currentLabels.length && newLabels.every((label, i) => label === currentLabels[i]);
      if (unchanged) {
        if (messageEl) messageEl.hidden = true;
        return;
      }

      const previouslyChecked = container.querySelector('input[type="radio"]:checked');
      const previouslyCheckedLabel = previouslyChecked ? previouslyChecked.value : null;

      container.innerHTML = '';
      if (messageEl) messageEl.hidden = true;

      const propertyName = PROPERTY_NAME_BY_METHOD[methodKey];
      const formId = this.formId;

      slots.forEach((slot, index) => {
        const uniqueId = `${methodKey}-slot-${index}-${Math.random().toString(36).slice(2, 8)}`;
        const label = formatSlotLabel(slot);

        const wrapper = document.createElement('label');
        wrapper.className = 'xo-field-radio xo-field-radio--boxed xo-delivery-pickup__slot';
        wrapper.setAttribute('for', uniqueId);

        const input = document.createElement('input');
        input.type = 'radio';
        input.className = 'xo-field-radio__input';
        input.id = uniqueId;
        input.name = `properties[${propertyName}]`;
        input.value = label;
        input.required = true;
        if (label === previouslyCheckedLabel) input.checked = true;
        if (formId) input.setAttribute('form', formId);

        const labelSpan = document.createElement('span');
        labelSpan.className = 'xo-field-radio__label';
        labelSpan.textContent = label;

        wrapper.append(input, labelSpan);
        container.appendChild(wrapper);
      });
    }

    /** Re-evaluates date bounds and any currently rendered slots. Called on
     * an interval so an open tab reacts to slots expiring in real time. */
    refreshAvailability() {
      this.applyDateBounds();
      METHOD_KEYS.forEach((methodKey) => {
        const dateInput = this.dateInputs[methodKey];
        if (dateInput && dateInput.value) {
          this.renderSlots(methodKey);
        }
      });
      this.updateSubmitState();
    }

    startAvailabilityWatcher() {
      this._watcherId = setInterval(() => this.refreshAvailability(), AVAILABILITY_WATCH_INTERVAL_MS);
    }

    isSelectionComplete() {
      if (!this.selectedMethod) return false;
      const dateInput = this.dateInputs[this.selectedMethod];
      const container = this.slotContainers[this.selectedMethod];
      const hasDate = Boolean(dateInput && dateInput.value);
      const hasTime = Boolean(container && container.querySelector('input[type="radio"]:checked'));
      const hasRecipientName = Boolean(this.recipientNameInput && this.recipientNameInput.value.trim());
      if (!hasDate || !hasTime || !hasRecipientName) return false;

      if (this.selectedMethod === 'delivery') {
        const hasRecipientPhone = Boolean(this.recipientPhoneInput && this.recipientPhoneInput.value.trim());
        if (!hasRecipientPhone) return false;
      }

      return true;
    }

    /** Keeps the Add to Cart button's visual + functional disabled state in
     * sync with the current selection. The native "required"/"form" wiring
     * in the markup independently blocks submission even if this ever gets
     * out of sync, so this is a UX layer on top of a functional guarantee.
     *
     * Deliberately does NOT touch the `xo-disabled` attribute on the
     * <xo-cart-add> element. The vendor framework only attaches its click
     * listener once, the first time <xo-cart-add> connects, and skips
     * attaching it entirely if `xo-disabled` is present at that exact
     * moment — it never re-attaches later even if the attribute is
     * removed. Since our element starts "incomplete" on every load, toggling
     * `xo-disabled` here raced against that one-time check: if <xo-cart-add>
     * happened to connect after this ran (more likely on a slow/cold first
     * load), its click listener would never be attached and the button
     * would silently do nothing for the rest of the page's life, even once
     * the selection became complete and this attribute was removed again —
     * exactly the "works after a refresh" symptom. The native `disabled`
     * attribute on the inner <button> is sufficient on its own: disabled
     * buttons never dispatch click events, so it blocks submission (and
     * unblocks it later) without ever depending on that fragile timing. */
    updateSubmitState() {
      const complete = this.isSelectionComplete();

      if (this.submitButton) {
        this.submitButton.disabled = !complete;
      }
      if (this.submitEl) {
        this.submitEl.classList.toggle('xo-delivery-pickup__submit--disabled', !complete);
      }
      if (this.submitHint) {
        this.submitHint.hidden = complete;
      }
    }
  }

  if (!window.customElements.get('product-delivery-pickup')) {
    window.customElements.define('product-delivery-pickup', ProductDeliveryPickup);
  }
})();
